/**
 * SlideEditorRegistry.jsx
 * Central registry + SlideContentEditor dispatcher.
 */
import React, { useState, useEffect, useCallback } from 'react';
import BulletListEditor from './editors/BulletListEditor';
import DynamicTableEditor from './editors/DynamicTableEditor';
import {
    StageActionEditor, TimelineEditor, JourneyEditor, DetailedJourneyEditor,
    StageDescriptionEditor, HeaderContentListEditor, HeadlineSubtextEditor,
    QAEditor, CaseStudyEditor
} from './editors/RowListEditors';
import {
    CenterTextListEditor, MatrixEditor, DetailedMatrixEditor,
    BeforeAfterEditor, BigNumberEditor, ChartSidebarEditor,
    SectionDividerEditor, GenericJsonFormEditor
} from './editors/ComplexEditors';
import { normalizeSlideContent, validateSlideContent } from '../utils/slideContentNormalizer';

// ─── Central registry ─────────────────────────────────────────────────────────
const SLIDE_EDITOR_REGISTRY = {
    bullets: BulletListEditor,
    table: DynamicTableEditor,
    timeline: TimelineEditor,
    chevron: StageActionEditor,
    journey: JourneyEditor,
    detailed_journey: DetailedJourneyEditor,
    funnel: StageDescriptionEditor,
    labeled_funnel: StageDescriptionEditor,
    cycle: CenterTextListEditor,
    radial_hub: CenterTextListEditor,
    matrix: MatrixEditor,
    detailed_matrix: DetailedMatrixEditor,
    venn: HeaderContentListEditor,
    pyramid: HeaderContentListEditor,
    spotlight: HeadlineSubtextEditor,
    before_after: BeforeAfterEditor,
    case_study: CaseStudyEditor,
    qa: QAEditor,
    big_number: BigNumberEditor,
    chart_sidebar: ChartSidebarEditor,
    section_divider: SectionDividerEditor,
};

// ─── Human-readable archetype label map ──────────────────────────────────────
const ARCHETYPE_LABELS = {
    bullets: 'Bullet List', table: 'Table', timeline: 'Timeline',
    chevron: 'Chevron Flow', journey: 'Journey', detailed_journey: 'Detailed Journey',
    funnel: 'Funnel', labeled_funnel: 'Labeled Funnel', cycle: 'Cycle',
    radial_hub: 'Radial Hub', matrix: 'Matrix', detailed_matrix: 'Detailed Matrix',
    venn: 'Venn', pyramid: 'Pyramid', spotlight: 'Spotlight',
    before_after: 'Before & After', case_study: 'Case Study',
    qa: 'Q & A', big_number: 'Big Number', chart_sidebar: 'Chart Sidebar',
    section_divider: 'Section Divider',
};

// ─── Archetype icon colors ────────────────────────────────────────────────────
const ARCHETYPE_COLORS = {
    bullets: '#6366F1', table: '#0EA5E9', timeline: '#F59E0B',
    chevron: '#10B981', journey: '#8B5CF6', detailed_journey: '#8B5CF6',
    funnel: '#EF4444', labeled_funnel: '#EF4444', cycle: '#06B6D4',
    radial_hub: '#06B6D4', matrix: '#FF5526', detailed_matrix: '#FF5526',
    venn: '#EC4899', pyramid: '#14B8A6', spotlight: '#8B5CF6',
    before_after: '#64748B', case_study: '#0EA5E9', qa: '#6366F1',
    big_number: '#F59E0B', section_divider: '#94A3B8',
};

// ─── SlideContentEditor ──────────────────────────────────────────────────────
const SlideContentEditor = ({ slide, onSave, isSaving }) => {
    const archetype = slide?.archetype || slide?.layout || 'bullets';
    const isStatic = slide?.is_static ?? slide?.type === 'static';
    const readOnly = isStatic;

    const [localValue, setLocalValue] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [hasSavedDraft, setHasSavedDraft] = useState(false);

    useEffect(() => {
        setLocalValue(normalizeSlideContent(slide));
        setIsDirty(false);
        setValidationErrors([]);
        setHasSavedDraft(false);
    }, [slide?.slide_id]);

    const handleChange = useCallback((val) => {
        setLocalValue(val);
        setIsDirty(true);
        setValidationErrors([]);
    }, []);

    const handleSave = () => {
        const errors = validateSlideContent(archetype, localValue);
        if (errors.length > 0) { setValidationErrors(errors); return; }
        onSave(localValue);
        setIsDirty(false);
        setHasSavedDraft(true);
    };

    const handleReset = () => {
        setLocalValue(normalizeSlideContent(slide));
        setIsDirty(false);
        setValidationErrors([]);
        setHasSavedDraft(false);
    };

    let resolvedArchetype = archetype;
    if (archetype === 'auto') {
        resolvedArchetype = (localValue && localValue.column_headers) ? 'table' : 'bullets';
    }
    const EditorComponent = SLIDE_EDITOR_REGISTRY[resolvedArchetype] || GenericJsonFormEditor;
    const humanLabel = ARCHETYPE_LABELS[resolvedArchetype] || resolvedArchetype.replace(/_/g, ' ');
    const accentColor = ARCHETYPE_COLORS[resolvedArchetype] || '#6366F1';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* ── Header bar ── */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 0 12px 0', marginBottom: 12,
                borderBottom: '1px solid #F1F5F9',
            }}>
                {/* Left: archetype + type pills */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* Archetype pill */}
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 10px', borderRadius: 99,
                        background: `${accentColor}15`, color: accentColor,
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: accentColor, display: 'inline-block' }} />
                        {humanLabel}
                    </span>

                    {/* Static / AI pill */}
                    <span style={{
                        padding: '3px 10px', borderRadius: 99,
                        background: isStatic ? '#F1F5F9' : '#F0FDF4',
                        color: isStatic ? '#64748B' : '#15803D',
                        fontSize: 11, fontWeight: 700,
                    }}>
                        {isStatic ? 'Static' : 'AI Generated'}
                    </span>

                    {/* Unsaved indicator */}
                    {isDirty && !readOnly && (
                        <span style={{
                            padding: '3px 8px', borderRadius: 99,
                            background: '#FFF1EC', color: '#E9471D',
                            fontSize: 10, fontWeight: 700,
                        }}>
                            ● Unsaved
                        </span>
                    )}
                </div>

                {/* Right: action buttons (only when dirty) */}
                {!readOnly && isDirty && (
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button
                            onClick={handleReset}
                            disabled={isSaving}
                            style={{
                                padding: '5px 14px', border: '1px solid #E2E8F0',
                                borderRadius: 6, background: '#fff', color: '#64748B',
                                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            Reset
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            style={{
                                padding: '5px 16px', border: 'none', borderRadius: 6,
                                background: isSaving ? '#E2E8F0' : 'linear-gradient(135deg, #FF5526 0%, #FF5526CC 100%)',
                                color: isSaving ? '#94A3B8' : '#fff',
                                fontSize: 12, fontWeight: 700,
                                cursor: isSaving ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: isSaving ? 'none' : '0 2px 8px rgba(255, 85, 38, 0.25)',
                            }}
                        >
                            {isSaving ? 'Saving…' : 'Save Draft'}
                        </button>
                    </div>
                )}
            </div>

            {/* ── Validation errors ── */}
            {validationErrors.length > 0 && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                    {validationErrors.map((e, i) => (
                        <p key={i} style={{ color: '#DC2626', fontSize: 12, margin: '2px 0' }}>• {e}</p>
                    ))}
                </div>
            )}

            {/* ── Dynamic editor ── */}
            <div>
                {localValue !== null ? (
                    <EditorComponent
                        slide={slide}
                        value={localValue}
                        onChange={handleChange}
                        readOnly={readOnly}
                    />
                ) : (
                    <div style={{ padding: 20, textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
                        Loading editor...
                    </div>
                )}
            </div>
        </div>
    );
};

export { SLIDE_EDITOR_REGISTRY };
export default SlideContentEditor;
